import {Table, Card, Tabs, Spin, Select, Input, Space, Row, Col} from 'antd';
import React, { useCallback, useState, useEffect } from 'react';
import {ListBase, ListProps, useListContext, Record, useDataProvider, useQueryWithStore} from 'react-admin';
import ReactJson from '@microlink/react-json-view';
import SkeletonTable, { SkeletonTableColumnsType } from '../Common/SkeletonTable';
import { ExtendedDataProvider, SelectItem } from '../../types'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import '../../assets/css/github_markdown.css';
import ServiceExample from './ServiceExample';
import {onExpandIcon, tableOnChangeGenerator, useExpandableRows} from "../Common/tableUtils";
import ServiceTagFilter from "../Filters/ServiceTagFilter";
import {getTagForServiceState, renderNameAndTagWithEllipsis} from "../Common/Tags";

const { Search } = Input;
const { Option } = Select;


const ListView = () => {
  const { ids, data, loading, loaded, total, perPage, page, setPage, setPerPage, setSort, filterValues, setFilters } = useListContext();

  const dataProvider: ExtendedDataProvider = useDataProvider();
  const [readmeData, setReadmeData] = useState<any>({});

  const [owners, setOwners] = useState<SelectItem[] | null>(null);
  const tableData: Record[] = []

  const { expandedRowKeys, onExpand, toggleRowExpansion } = useExpandableRows('name');

  // Fetch readme when a row is expanded from URL hash
  useEffect(() => {
    if (expandedRowKeys.length > 0) {
      const serviceName = expandedRowKeys[0];
      const serviceId = Object.values(data).find((item: any) => item.name === serviceName)?.id;
      if (serviceId) {
        fetchReadme(serviceId as number);
      }
    }
  }, [expandedRowKeys, data]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text: any, record: any) => <>{text+"   "}{getTagForServiceState(record.state)}<br/>{renderNameAndTagWithEllipsis(record.name, record.tags, 25)}</>
    },
    {
      title: 'Description',
      key: 'description',
      render: (text: any, record: any) => record?.schema.schema.description
    },
    {
      title: 'Service Owner',
      dataIndex: ['owner', 'name'],
      key: 'owner',
    },
  ]

  const { data: ownerTeamData } = useQueryWithStore({
    type: 'getList',
    resource: 'serviceowner_teams',
    payload: {
      pagination: { page: 1, perPage: 20 },
      sort: { field: 'name', order: 'asc' },
      filter: {}
    }
  });

  const handleSelectTeam = (value: string) => {
    setFilters({ ...filterValues, 'owner_id': value }, {})
  }

  useEffect(() => {
    const ownerFilter: any[] = []
    if (ownerTeamData) {
      for (let item of ownerTeamData) {
        ownerFilter.push({ 'text': item.name, 'value': item.id })
      }
      setOwners(ownerFilter);
    }
  }, [ownerTeamData])

  const expandedRowRender = (record: any) => {
    return (
      <Card key={record.id}>
        <Space orientation={"vertical"}>
        <Tabs defaultActiveKey={record.has_md ? record.id + "_readme" : record.id + "_schema"} type='card' items={[
          ...(record.has_md ? [{
            key: record.id + "_readme",
            label: 'Readme',
            style: { padding: '20px' },
            children: (
              <>
                {!readmeData[record.id] && <Spin style={{ 'width': '100%' }} />}
                {readmeData[record.id] && <ReactMarkdown className='markdown-body' rehypePlugins={[rehypeRaw, remarkGfm]} children={String(readmeData[record.id])} />}
              </>
            ),
          }] : []),
          {
            key: record.id + "_schema",
            label: 'Schema',
            children: <ReactJson collapseStringsAfterLength={50} style={{ padding: '5px 15px'}} src={record.schema.schema} displayArrayKey={false} displayDataTypes={false} name={false} displayObjectSize={false} />,
          },
          {
            key: record.id + "_example",
            label: 'Example',
            children: <ServiceExample style={{ padding: '5px 15px' }} record={record} />,
          },
        ]} />
        </Space>
      </Card>
    )
  }

  const fetchReadme = (service_id: number) => {
    dataProvider.getActionNonJson('browse_services', { id: service_id, verb: 'docs' })
        .then((response: any) => {
          setReadmeData((prev: any) => ({...prev,  [service_id]: response.data.replace(/\\n/gi, '  \n')}))
        })
        .catch((error: any) => { console.log(error) })
  }

  // Wrapper to combine onExpand from hook and fetchReadme
  const handleExpand = useCallback((expanded: boolean, record: any) => {
    onExpand(expanded, record);
    if (expanded) {
      fetchReadme(record.id);
    }
  }, [onExpand, dataProvider]);

  const onSearch = (value: string) => {
    setFilters({ ...filterValues, name: value }, {});
  }

  ids.forEach(id => {
    tableData.push(data[id])
  });
  return (
    <Card
      bordered={false}
      className="criclebox tablespace mb-24"
    >
      <Row gutter={[8, 0]} style={{padding: '16px'}}>
        <Col span={8}>
          <Search
            className='item-search'
            style={{ borderRadius: '8px', overflow: 'hidden' }}
            allowClear
            placeholder="Search by name"
            onSearch={onSearch}
            enterButton
          />
        </Col>
        <Col span={8}>
          <Select
            data-cy="team-selector"
            defaultValue={filterValues.owner_id}
            placeholder="Select Team"
            style={{ width: '100%' }}
            onChange={handleSelectTeam}
            className="filter-select"
          >
            <Option key={0} value="">All Teams</Option>
            {owners && owners.map(team => <Option key={team.value} value={team.value}>{team.text}</Option>)}
          </Select>
        </Col>
        <Col span={8}>
          <ServiceTagFilter filterValues={filterValues} setFilterValues={setFilters} />
        </Col>
      </Row>
      <SkeletonTable loading={loading} columns={columns as SkeletonTableColumnsType[]}>
        <Table
          rowKey='name'
          columns={columns}
          dataSource={tableData}
          pagination={{
            pageSizeOptions: [25, 50, 100],
            onShowSizeChange: (_current, size) => setPerPage(size),
            showSizeChanger: true,
            total: total,
            pageSize: perPage,
            current: page,
            className: "pagination-padding",
          }}
          onChange={tableOnChangeGenerator(setSort, page, setPage)}
          expandable={{
            expandedRowRender,
            expandRowByClick: true,
            onExpand: handleExpand,
            expandedRowKeys,
            expandIcon: onExpandIcon(toggleRowExpansion, handleExpand),
          }}
          onRow={(record) => { return { onClick: () => { toggleRowExpansion(record.name) } } }}
        />
      </SkeletonTable>
    </Card>
  )
}

const ServiceCatalogueList = (props: ListProps) => (
  <ListBase perPage={25} {...props}>
    <ListView />
  </ListBase>
)

export default ServiceCatalogueList;
